'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONFIG } from '@frameio/config';
import { useAdobeIMS } from '@frameio/connections/AdobeIMS/useAdobeIMS';
import { UBS_MODAL_AUTH_PARAM } from '../constants';
import { useRegisterUbsUserAccessTokenMutation } from '../gql/mutations/generated/RegisterUbsUserAccessToken';
import { useBrandsForAccountQuery } from '../gql/queries/generated/BrandsForAccount';

export type Brand = {
  libraryUrn: string;
  name: string;
};

type UseBrandsForUbsReturn = {
  brands: Brand[];
  error: boolean;
  loading: boolean;
  needsSignIn: boolean;
  signIn: () => void;
};

const FAILED_PRECONDITION = 'FAILED_PRECONDITION';

function getUbsModalAuthRedirectUri() {
  const redirectUri = new URL(window.location.href);
  redirectUri.searchParams.set(UBS_MODAL_AUTH_PARAM, 'true');
  redirectUri.hash = '';
  return redirectUri.toString();
}

export function useBrandsForUbs({
  accountId,
}: {
  accountId: string | undefined;
}): UseBrandsForUbsReturn {
  const [imsRegistering, setImsRegistering] = useState(false);
  const [registerFailed, setRegisterFailed] = useState(false);

  // Prevent duplicate register mutations from repeated effect runs.
  const hasAttemptedRegistrationRef = useRef(false);

  const {
    getAccessToken,
    hasCompletedModalSignIn,
    initialized,
    signIn: imsSignIn,
  } = useAdobeIMS({
    config: CONFIG.adobeIMS.brandValidation,
    modalSignIn: true,
  });

  // The useBrandsForAccountQuery also serves as an authorization check,
  // returning a FAILED_PRECONDITION error if the user is not authorized.
  const {
    data: brandsData,
    error: brandsError,
    loading: brandsLoading,
    refetch: refetchBrands,
  } = useBrandsForAccountQuery({
    skip: !accountId,
    variables: accountId ? { accountId } : undefined,
  });

  const needsRegistration = Boolean(
    brandsError?.graphQLErrors?.some(
      (err) => err.extensions?.code === FAILED_PRECONDITION
    )
  );
  const hasUnrecoverableError = Boolean(brandsError) && !needsRegistration;

  const needsSignIn =
    needsRegistration &&
    initialized &&
    (!hasCompletedModalSignIn || registerFailed);

  const [registerMutation] = useRegisterUbsUserAccessTokenMutation();

  // Conditionally register the UAT with Massdriver.
  useEffect(() => {
    if (!needsRegistration) return undefined;
    if (!initialized) return undefined;
    if (!accountId) return undefined;
    if (!hasCompletedModalSignIn) return undefined;
    if (hasAttemptedRegistrationRef.current) return undefined;

    let cancelled = false;
    hasAttemptedRegistrationRef.current = true;
    setImsRegistering(true);
    setRegisterFailed(false);

    const registerAfterModalSignIn = async () => {
      try {
        const token = await getAccessToken();
        if (cancelled) return;
        if (!token?.token) {
          hasAttemptedRegistrationRef.current = false;
          setRegisterFailed(true);
          return;
        }
        await registerMutation({
          variables: { input: { accountId, uat: token.token } },
        });
        if (cancelled) return;
        await refetchBrands();
      } catch {
        if (cancelled) return;
        hasAttemptedRegistrationRef.current = false;
        setRegisterFailed(true);
      } finally {
        if (!cancelled) {
          setImsRegistering(false);
        }
      }
    };

    registerAfterModalSignIn();

    return () => {
      cancelled = true;
      setImsRegistering(false);
    };
  }, [
    needsRegistration,
    initialized,
    accountId,
    hasCompletedModalSignIn,
    getAccessToken,
    registerMutation,
    refetchBrands,
  ]);

  const brands = useMemo<Brand[]>(
    () =>
      (brandsData?.account?.brands ?? []).flatMap((brand) =>
        brand.libraryUrn != null && brand.name != null
          ? [{ libraryUrn: brand.libraryUrn, name: brand.name }]
          : []
      ),
    [brandsData]
  );

  const signIn = useCallback(() => {
    // `profile_filter.findFirst: false` is what forces the Adobe account +
    // IMS org picker. Without it, IMS silently auto-picks a profile when
    // the user already has a valid Adobe SSO session and the popup closes
    // immediately with no UI. See:
    //   https://wiki.corp.adobe.com/display/ims/Profile+Filter+syntax
    imsSignIn({
      dc: true,
      profile_filter: { findFirst: false },
      redirect_uri: getUbsModalAuthRedirectUri(),
    });
  }, [imsSignIn]);

  const loading = !initialized || brandsLoading || imsRegistering;

  return {
    brands,
    error: hasUnrecoverableError,
    loading,
    needsSignIn,
    signIn,
  };
}
